/**
 * How long a signed-in session may last on this browser (MYAC-1626, pentest
 * finding DEV-01). Firebase renews its ID token forever with a refresh token that
 * does not expire, so the only limit is the one this app enforces: a sign-in time
 * stored at login, compared against a maximum age.
 *
 * - « Remember me » (local persistence): the time sits in localStorage, 30 days.
 * - Without it (session persistence): the time sits in sessionStorage, 8 hours —
 *   closing the browser still ends the session sooner, as before.
 */

export const REMEMBER_ME_DURATION_MS = 30 * 24 * 60 * 60 * 1000 // 30 days
export const SESSION_DURATION_MS = 8 * 60 * 60 * 1000 // 8 hours

/** An open tab is re-checked this often, and whenever it is shown again. */
export const SESSION_CHECK_INTERVAL_MS = 30 * 1000

/**
 * Lowers both limits on this browser, in milliseconds, so the sign-out can be
 * watched without waiting 8 hours (the ticket's test override). It can only
 * shorten a session: a value above a limit is ignored, so it is no way around it.
 */
export const SESSION_LIMIT_OVERRIDE_KEY = 'mac_session_limit_override_ms'

export const rememberMeTimestampKey = (uid: string) => `auth_login_timestamp_${uid}`
export const sessionStartKey = (uid: string) => `auth_session_persistence_${uid}`

export type SessionVerdict = 'valid' | 'expired' | 'unmarked'

interface Storages {
  local: Pick<Storage, 'getItem'>
  session: Pick<Storage, 'getItem' | 'setItem'>
}

function limitMs(base: number, local: Storages['local']): number {
  const override = Number(local.getItem(SESSION_LIMIT_OVERRIDE_KEY))
  return Number.isFinite(override) && override > 0 && override < base ? override : base
}

/**
 * `unmarked`: neither time is stored, which a session signed in before these
 * markers existed looks like — the caller decides (a fresh login is not one).
 */
export function readSessionVerdict(
  uid: string,
  now: number,
  storages: Storages = { local: localStorage, session: sessionStorage },
): SessionVerdict {
  const { local, session } = storages
  // This tab's own sign-in wins: a remember-me login removes it, so a 30-day time
  // beside it can only be a leftover of an earlier session on this browser.
  const marker = session.getItem(sessionStartKey(uid))
  if (marker === null) {
    const rememberedAt = local.getItem(rememberMeTimestampKey(uid))
    if (!rememberedAt) return 'unmarked'
    const elapsed = now - parseInt(rememberedAt, 10)
    return elapsed > limitMs(REMEMBER_ME_DURATION_MS, local) ? 'expired' : 'valid'
  }
  let startedAt = parseInt(marker, 10)
  if (!Number.isFinite(startedAt)) {
    // A tab signed in before MYAC-1626 holds the marker 'true', with no time: its
    // 8 hours start now rather than signing out everyone at the deploy.
    startedAt = now
    session.setItem(sessionStartKey(uid), String(now))
  }
  return now - startedAt > limitMs(SESSION_DURATION_MS, local) ? 'expired' : 'valid'
}
